import type { SurveyBuilderManager } from '@/state/surveys/useSurveyBuilder';
import type { SurveyScoreScale } from 'definitions';
import type { DraftQuestion, QuestionKind } from '@/state/surveys/survey-questions';
import { useRef } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import { SURVEY_SCALE_END_LABELS, SURVEY_SCORE_RANGES, SURVEY_SCORE_SCALES } from 'definitions';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  MAX_END_LABEL,
  MAX_OPTIONS,
  MAX_QUESTIONS,
  QUESTION_KIND_LABEL,
  addableKinds,
  isAnchor,
  moveQuestion,
  newQuestion,
  optionsOf,
  questionProblem,
  scaleFor,
} from '@/state/surveys/survey-questions';

type StepQuestionsProps = { manager: SurveyBuilderManager };

const FIELD_INPUT = 'border-line bg-field text-foreground h-9 rounded-lg text-body';
const ICON_BUTTON = 'text-dim hover:text-foreground hover:bg-hover h-7 w-7 rounded-lg p-0';

const SCALE_LABEL: Record<SurveyScoreScale, string> = {
  NPS: '0–10',
  CSAT: '1–5 stars',
  CES: '1–7',
};

/*
 * None of these fields carry a name attribute. useForm maps a name to a single
 * state path, and an array of questions needs index-aware writes, so every field
 * here is controlled through updateDraft instead of the DOM.
 */
export const StepQuestions = ({ manager }: StepQuestionsProps) => {
  const { draft, updateDraft, questionsLocked } = manager;
  const addable: QuestionKind[] = addableKinds(draft.type);
  const isCustom = draft.type === 'Custom';

  const setQuestion = (index: number, change: (question: DraftQuestion) => DraftQuestion) =>
    updateDraft((state) => ({
      ...state,
      questions: state.questions.map((question, i) => (i === index ? change(question) : question)),
    }));

  const setConfig = (index: number, config: Record<string, unknown>) =>
    setQuestion(index, (question) => ({ ...question, config: { ...question.config, ...config } }));

  const setOptions = (index: number, options: string[]) => setConfig(index, { options });

  // An emptied end label is left out, so the scale's default shows instead and the
  // API never sees an empty string.
  const setEndLabel = (index: number, key: 'lowLabel' | 'highLabel', value: string) =>
    setQuestion(index, (question) => {
      const { [key]: _dropped, ...rest } = question.config ?? {};
      return { ...question, config: value.trim() ? { ...rest, [key]: value } : rest };
    });

  const add = (kind: QuestionKind) => () =>
    updateDraft((state) => ({ ...state, questions: [...state.questions, newQuestion(kind)] }));

  const remove = (index: number) => () =>
    updateDraft((state) => ({ ...state, questions: state.questions.filter((_, i) => i !== index) }));

  const listRef = useRef<HTMLDivElement>(null);

  // Cards are keyed by position, so after a move the focused button would belong to
  // the question that was swapped into its place, and pressing again would undo the
  // move. Focus follows the moved question instead, to the same arrow if it is still
  // enabled there, else the other one.
  const move = (from: number, to: number, direction: 'up' | 'down') => () => {
    updateDraft((state) => ({ ...state, questions: moveQuestion({ questions: state.questions, from, to }) }));
    requestAnimationFrame(() => {
      const card = listRef.current?.querySelector(`[data-question="${to}"]`);
      const same = card?.querySelector<HTMLButtonElement>(`[data-move="${direction}"]:not(:disabled)`);
      (same ?? card?.querySelector<HTMLButtonElement>('[data-move]:not(:disabled)'))?.focus();
    });
  };

  const anchorIndex = draft.questions.findIndex((_, index) => isAnchor(draft, index));

  // Read-only once the survey has run: its responses point at these questions, and
  // the API refuses to replace them. Saying so here beats a 409 at save time.
  if (questionsLocked) {
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-foreground m-0 text-lede font-medium">Questions</h2>
          <p className="supporting m-0">
            These are fixed now that the survey has run, because its responses are tied to them. Duplicate the survey to
            ask different questions.
          </p>
        </div>
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {draft.questions.map((question, index) => (
            <li key={index} className="bg-panel2 border-line rounded-card border p-3.5">
              <p className="micro-label m-0">
                {index + 1}. {QUESTION_KIND_LABEL[question.kind]}
                {question.required ? ', required' : ''}
              </p>
              <p className="text-foreground m-0 mt-1 text-meta break-words">{question.label}</p>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="space-y-4" ref={listRef}>
      <div className="space-y-1">
        <h2 className="text-foreground m-0 text-lede font-medium">Questions</h2>
        <p className="supporting m-0">
          {isCustom
            ? `Up to ${MAX_QUESTIONS} questions, shown one at a time in this order. Fewer questions keep completion high.`
            : `The ${draft.type} score question comes first and is always asked. Add up to ${MAX_QUESTIONS - 1} more after it.`}
        </p>
      </div>

      {draft.questions.map((question, index) => {
        const anchored = index === anchorIndex;
        // The anchor stays first: nothing may move above it, and it never moves.
        const canMoveUp = !anchored && index > 0 && index - 1 !== anchorIndex;
        const canMoveDown = !anchored && index < draft.questions.length - 1;
        const problem = questionProblem(question);
        const scale = scaleFor(question, draft.type);
        const range = SURVEY_SCORE_RANGES[scale];
        const [lowDefault, highDefault] = SURVEY_SCALE_END_LABELS[scale];
        const options = optionsOf(question);

        return (
          <div key={index} data-question={index} className="bg-panel2 border-line rounded-card space-y-3 border p-3.5">
            <div className="flex items-center justify-between gap-2">
              <span className="micro-label">
                {index + 1}. {QUESTION_KIND_LABEL[question.kind]}
                {question.kind === 'score' && ` (${range.min}–${range.max})`}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  className={ICON_BUTTON}
                  onClick={move(index, index - 1, 'up')}
                  disabled={!canMoveUp}
                  data-move="up"
                >
                  <ArrowUp className="h-4 w-4" />
                  <span className="sr-only">Move question {index + 1} up</span>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className={ICON_BUTTON}
                  onClick={move(index, index + 1, 'down')}
                  disabled={!canMoveDown}
                  data-move="down"
                >
                  <ArrowDown className="h-4 w-4" />
                  <span className="sr-only">Move question {index + 1} down</span>
                </Button>
                {!anchored && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-dim hover:text-danger hover:bg-danger-bg h-7 w-7 rounded-lg p-0"
                    onClick={remove(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Remove question {index + 1}</span>
                  </Button>
                )}
              </div>
            </div>

            <Input
              value={question.label}
              onChange={(ev) => setQuestion(index, (current) => ({ ...current, label: ev.target.value }))}
              maxLength={500}
              aria-label={`Question ${index + 1}`}
              aria-describedby={problem ? `question-${index}-problem` : undefined}
              aria-invalid={problem ? true : undefined}
              className={FIELD_INPUT}
            />

            {question.kind === 'score' && isCustom && (
              <div
                className="flex flex-wrap items-center gap-1.5"
                role="group"
                aria-labelledby={`question-${index}-scale`}
              >
                <span id={`question-${index}-scale`} className="micro-label mr-1">
                  Scale
                </span>
                {SURVEY_SCORE_SCALES.map((candidate) => (
                  <button
                    key={candidate}
                    type="button"
                    onClick={() => setConfig(index, { scale: candidate })}
                    aria-pressed={scale === candidate}
                    className={cn(
                      'micro-label rounded-lg border px-2.5 py-1 transition-colors duration-150',
                      scale === candidate
                        ? 'bg-sel-bg border-sel-line text-foreground'
                        : 'border-line text-dim hover:bg-hover hover:text-foreground',
                    )}
                  >
                    {SCALE_LABEL[candidate]}
                  </button>
                ))}
              </div>
            )}

            {question.kind === 'score' && (
              <div className="grid gap-2 sm:grid-cols-2">
                <Input
                  value={String(question.config?.lowLabel ?? '')}
                  onChange={(ev) => setEndLabel(index, 'lowLabel', ev.target.value)}
                  placeholder={lowDefault}
                  maxLength={MAX_END_LABEL}
                  aria-label={`Question ${index + 1}, label for ${range.min}`}
                  className={FIELD_INPUT}
                />
                <Input
                  value={String(question.config?.highLabel ?? '')}
                  onChange={(ev) => setEndLabel(index, 'highLabel', ev.target.value)}
                  placeholder={highDefault}
                  maxLength={MAX_END_LABEL}
                  aria-label={`Question ${index + 1}, label for ${range.max}`}
                  className={FIELD_INPUT}
                />
              </div>
            )}

            {(question.kind === 'single choice' || question.kind === 'multiple choice') && (
              <div className="space-y-1.5">
                {options.map((option, optionIndex) => (
                  <div key={optionIndex} className="flex items-center gap-1.5">
                    <Input
                      value={option}
                      onChange={(ev) =>
                        setOptions(
                          index,
                          options.map((value, i) => (i === optionIndex ? ev.target.value : value)),
                        )
                      }
                      placeholder={`Option ${optionIndex + 1}`}
                      maxLength={256}
                      aria-label={`Question ${index + 1}, option ${optionIndex + 1}`}
                      className={FIELD_INPUT}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      className={ICON_BUTTON}
                      disabled={options.length <= 2}
                      onClick={() =>
                        setOptions(
                          index,
                          options.filter((_, i) => i !== optionIndex),
                        )
                      }
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">
                        Remove option {optionIndex + 1} of question {index + 1}
                      </span>
                    </Button>
                  </div>
                ))}
                {options.length < MAX_OPTIONS && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOptions(index, [...options, ''])}
                    className="text-dim hover:text-foreground hover:bg-hover h-8 rounded-lg px-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add option
                  </Button>
                )}
              </div>
            )}

            {question.kind === 'open text' && (
              <Input
                value={String(question.config?.placeholder ?? '')}
                onChange={(ev) => setConfig(index, { placeholder: ev.target.value })}
                placeholder="Placeholder text (optional)"
                maxLength={200}
                aria-label="Placeholder text"
                className={FIELD_INPUT}
              />
            )}

            <div className="flex items-center justify-between gap-2">
              <label className="supporting flex items-center gap-2">
                <Switch
                  checked={question.required}
                  disabled={anchored}
                  onCheckedChange={(required) => setQuestion(index, (current) => ({ ...current, required }))}
                />
                {anchored ? 'Always required' : 'Required'}
              </label>
              {problem && (
                <p id={`question-${index}-problem`} className="text-danger m-0 text-micro">
                  {problem}
                </p>
              )}
            </div>
          </div>
        );
      })}

      {draft.questions.length < MAX_QUESTIONS && (
        <div className="flex flex-wrap gap-2">
          {addable.map((kind) => (
            <Button
              key={kind}
              type="button"
              variant="outline"
              onClick={add(kind)}
              className="border-line h-9 rounded-lg bg-transparent"
            >
              <Plus className="h-4 w-4" />
              {QUESTION_KIND_LABEL[kind]}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
